import { FilterValue } from 'antd/es/table/interface';
import {
  exportCsvConverter,
  pagableDataConverter,
} from 'data/convertors/general.converters';
import { TExportCsvDB, TExportCsvUI } from 'data/types/generic.types';
import {
  workshopTaskAssigneeGetConverter,
  workshopTaskGetConverter,
} from 'data/convertors/workshopTasks.convertors';
import {
  EWorkshopPartType,
  EWorkshopTaskStatus,
  EWorkshopTasksFilterColumn,
  TAddCustomWorkshopTaskRequest,
  TDeleteCustomWorkshopTaskRequest,
  TExportWorkshopTasksRequest,
  TGetWorkshopTaskAssigneesRequest,
  TGetWorkshopTasksRequest,
  TUpdateWorkshopTaskAssigneeRequest,
  TUpdateWorkshopTaskDueDateRequest,
  TUpdateWorkshopTaskNameRequest,
  TUpdateWorkshopTaskStatusRequest,
  TWorkshopTaskAssigneeDB,
  TWorkshopTaskAssigneeUI,
  TWorkshopTasksPageDB,
  TWorkshopTasksPageUI,
  TWorkshopTaskUI,
  WORKSHOP_TASKS_DEFAULT_PAGE_SIZE,
} from 'data/types/workshopTasks.types';
import { EWorkshopsUrls } from 'data/urls/workshops.url';
import { authSplitApi } from 'redux/helpers/slice.helpers';
import { downloadFile } from 'redux/services/fileServices';

export const workshopTasksUrl = `${EWorkshopsUrls.WORKSHOPS}/tasks`;
export const workshopTasksExportUrl = `${workshopTasksUrl}/export`;
export const workshopTaskAssigneesUrl = `${workshopTasksUrl}/users`;
export const workshopTaskStatusUrl = `${workshopTasksUrl}/status`;
export const workshopTaskDueDateUrl = `${workshopTasksUrl}/dueDate`;
export const workshopTaskNameUrl = `${workshopTasksUrl}/name`;
export const workshopTaskAssigneeUrl = `${workshopTasksUrl}/assignee`;

export const buildGetWorkshopTasksQuery = ({
  workshopId,
  page = 0,
  size = WORKSHOP_TASKS_DEFAULT_PAGE_SIZE,
  sortField,
  sortDirection,
  partType,
  status,
  assignee,
}: TGetWorkshopTasksRequest) => ({
  url: workshopTasksUrl,
  method: 'GET' as const,
  params: {
    workshopId,
    page,
    size,
    ...(sortField && sortDirection && { sortField, sortDirection }),
    ...(partType && partType.length && { partType }),
    ...(status && status.length && { status }),
    ...(assignee && { assignee }),
  },
});

export const transformGetWorkshopTasksResponse = (
  data: TWorkshopTasksPageDB,
): TWorkshopTasksPageUI => ({
  ...pagableDataConverter.fromDb(data),
  readonly: true,
  canRemove: false,
  canAdd: false,
  content: (data.content || []).map(task =>
    workshopTaskGetConverter.fromDb(task),
  ),
});

export const buildGetWorkshopTaskAssigneesQuery = ({
  workshopId,
  assignee,
}: TGetWorkshopTaskAssigneesRequest) => ({
  url: workshopTaskAssigneesUrl,
  method: 'GET' as const,
  params: { workshopId, assignee },
});

export const transformGetWorkshopTaskAssigneesResponse = (
  data: TWorkshopTaskAssigneeDB[],
): TWorkshopTaskAssigneeUI[] =>
  (data || []).map(workshopTaskAssigneeGetConverter.fromDb);

export const buildExportWorkshopTasksQuery = (
  body: TExportWorkshopTasksRequest,
) => ({
  url: workshopTasksExportUrl,
  method: 'POST' as const,
  body,
});

// FileDto { fileName, content: base64 } → Blob + browser download, matching
// the Attendance/Waitlist export endpoints in registrants.slice.
export const transformExportWorkshopTasksResponse = (
  data: TExportCsvDB,
): TExportCsvUI => {
  const convertedData = exportCsvConverter('csv').fromDb(data);
  downloadFile(convertedData.content, convertedData.fileName);
  return convertedData;
};

// POST /workshops/tasks returns the new task's ResourceId ({ value }).
export const buildAddCustomWorkshopTaskQuery = (
  body: TAddCustomWorkshopTaskRequest,
) => ({
  url: workshopTasksUrl,
  method: 'POST' as const,
  body,
});

// GROW-4338: same path as add, with the task identified in the body. The BE
// only deletes custom tasks ("Only custom tasks can be deleted").
export const buildDeleteCustomWorkshopTaskQuery = ({
  workshopId,
  taskId,
}: TDeleteCustomWorkshopTaskRequest) => ({
  url: workshopTasksUrl,
  method: 'DELETE' as const,
  body: { workshopId, taskId },
});

export const buildUpdateWorkshopTaskStatusQuery = ({
  workshopId,
  taskId,
  status,
}: TUpdateWorkshopTaskStatusRequest) => ({
  url: workshopTaskStatusUrl,
  method: 'PATCH' as const,
  body: { workshopId, taskId, status },
});

export const buildUpdateWorkshopTaskDueDateQuery = ({
  workshopId,
  taskId,
  dueDate,
}: TUpdateWorkshopTaskDueDateRequest) => ({
  url: workshopTaskDueDateUrl,
  method: 'PATCH' as const,
  body: { workshopId, taskId, dueDate },
});

export const buildUpdateWorkshopTaskNameQuery = ({
  workshopId,
  taskId,
  name,
}: TUpdateWorkshopTaskNameRequest) => ({
  url: workshopTaskNameUrl,
  method: 'PATCH' as const,
  body: { workshopId, taskId, name },
});

export const buildUpdateWorkshopTaskAssigneeQuery = ({
  workshopId,
  taskId,
  userId,
}: TUpdateWorkshopTaskAssigneeRequest) => ({
  url: workshopTaskAssigneeUrl,
  method: 'PATCH' as const,
  body: { workshopId, taskId, userId },
});

export const applyOptimisticWorkshopTaskUpdate = (
  draft: TWorkshopTasksPageUI,
  taskId: string,
  patch: Partial<TWorkshopTaskUI>,
): void => {
  const task = draft.content.find(t => t.id === taskId);
  if (!task) return;
  Object.assign(task, patch);
};

export type TWorkshopTasksFilterParams = {
  partType?: EWorkshopPartType[];
  status?: EWorkshopTaskStatus[];
  assignee?: string;
};

export const buildWorkshopTasksFilterParams = (
  filters: Record<string, FilterValue | null> | undefined,
): TWorkshopTasksFilterParams => {
  if (!filters) return {};
  const result: TWorkshopTasksFilterParams = {};
  const part = filters[EWorkshopTasksFilterColumn.PART];
  const status = filters[EWorkshopTasksFilterColumn.STATUS];
  const assignee = filters[EWorkshopTasksFilterColumn.ASSIGNEE];

  if (part && part.length) {
    result.partType = part.map(v => String(v) as EWorkshopPartType);
  }
  if (status && status.length) {
    result.status = status.map(v => String(v) as EWorkshopTaskStatus);
  }
  if (assignee && assignee.length) {
    const [first] = assignee;
    if (typeof first === 'string') {
      result.assignee = first;
    }
  }
  return result;
};

export const workshopTasksApi = authSplitApi('workshopTasks', [
  'workshop-tasks',
]).injectEndpoints({
  endpoints: build => ({
    getWorkshopTasks: build.query<
      TWorkshopTasksPageUI,
      TGetWorkshopTasksRequest
    >({
      query: buildGetWorkshopTasksQuery,
      providesTags: (_result, _error, { workshopId }) => [
        { type: 'workshop-tasks', id: workshopId },
      ],
      transformResponse: transformGetWorkshopTasksResponse,
    }),

    getWorkshopTaskAssignees: build.query<
      TWorkshopTaskAssigneeUI[],
      TGetWorkshopTaskAssigneesRequest
    >({
      query: buildGetWorkshopTaskAssigneesQuery,
      transformResponse: transformGetWorkshopTaskAssigneesResponse,
    }),

    exportWorkshopTasks: build.mutation<
      TExportCsvUI,
      TExportWorkshopTasksRequest
    >({
      query: buildExportWorkshopTasksQuery,
      transformResponse: transformExportWorkshopTasksResponse,
    }),

    addCustomWorkshopTask: build.mutation<
      { value: string } | void,
      TAddCustomWorkshopTaskRequest
    >({
      query: buildAddCustomWorkshopTaskQuery,
      // No optimistic insert: the BE assigns order/id, so refetch the list.
      invalidatesTags: (_result, _error, arg) => [
        { type: 'workshop-tasks', id: arg.workshopId },
      ],
    }),

    deleteCustomWorkshopTask: build.mutation<
      void,
      TDeleteCustomWorkshopTaskRequest
    >({
      query: buildDeleteCustomWorkshopTaskQuery,
      invalidatesTags: (_result, _error, arg) => [
        { type: 'workshop-tasks', id: arg.workshopId },
      ],
    }),

    updateWorkshopTaskStatus: build.mutation<
      void,
      TUpdateWorkshopTaskStatusRequest
    >({
      query: buildUpdateWorkshopTaskStatusQuery,
      async onQueryStarted(arg, { dispatch, getState, queryFulfilled }) {
        // Patch every cached getWorkshopTasks entry for this workshop (each
        // filter/search/sort combination is a separate cache key), so the
        // change shows on whatever view is currently on screen.
        const patches = workshopTasksApi.util
          .selectInvalidatedBy(getState(), [
            { type: 'workshop-tasks', id: arg.workshopId },
          ])
          .filter(entry => entry.endpointName === 'getWorkshopTasks')
          .map(({ originalArgs }) =>
            dispatch(
              workshopTasksApi.util.updateQueryData(
                'getWorkshopTasks',
                originalArgs as TGetWorkshopTasksRequest,
                draft => {
                  applyOptimisticWorkshopTaskUpdate(draft, arg.taskId, {
                    status: arg.status,
                  });
                },
              ),
            ),
          );
        try {
          await queryFulfilled;
        } catch {
          patches.forEach(patch => patch.undo());
        }
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: 'workshop-tasks', id: arg.workshopId },
      ],
    }),

    updateWorkshopTaskDueDate: build.mutation<
      void,
      TUpdateWorkshopTaskDueDateRequest
    >({
      query: buildUpdateWorkshopTaskDueDateQuery,
      async onQueryStarted(arg, { dispatch, getState, queryFulfilled }) {
        const patches = workshopTasksApi.util
          .selectInvalidatedBy(getState(), [
            { type: 'workshop-tasks', id: arg.workshopId },
          ])
          .filter(entry => entry.endpointName === 'getWorkshopTasks')
          .map(({ originalArgs }) =>
            dispatch(
              workshopTasksApi.util.updateQueryData(
                'getWorkshopTasks',
                originalArgs as TGetWorkshopTasksRequest,
                draft => {
                  applyOptimisticWorkshopTaskUpdate(draft, arg.taskId, {
                    dueDate: arg.dueDate ?? '',
                  });
                },
              ),
            ),
          );
        try {
          await queryFulfilled;
        } catch {
          patches.forEach(patch => patch.undo());
        }
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: 'workshop-tasks', id: arg.workshopId },
      ],
    }),

    // GROW-4278: rename a custom task. Same optimistic pattern as the inline
    // cell editors, so the new title shows immediately and rolls back on error.
    updateWorkshopTaskName: build.mutation<
      void,
      TUpdateWorkshopTaskNameRequest
    >({
      query: buildUpdateWorkshopTaskNameQuery,
      async onQueryStarted(arg, { dispatch, getState, queryFulfilled }) {
        const patches = workshopTasksApi.util
          .selectInvalidatedBy(getState(), [
            { type: 'workshop-tasks', id: arg.workshopId },
          ])
          .filter(entry => entry.endpointName === 'getWorkshopTasks')
          .map(({ originalArgs }) =>
            dispatch(
              workshopTasksApi.util.updateQueryData(
                'getWorkshopTasks',
                originalArgs as TGetWorkshopTasksRequest,
                draft => {
                  applyOptimisticWorkshopTaskUpdate(draft, arg.taskId, {
                    taskName: arg.name,
                  });
                },
              ),
            ),
          );
        try {
          await queryFulfilled;
        } catch {
          patches.forEach(patch => patch.undo());
        }
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: 'workshop-tasks', id: arg.workshopId },
      ],
    }),

    updateWorkshopTaskAssignee: build.mutation<
      void,
      TUpdateWorkshopTaskAssigneeRequest
    >({
      query: buildUpdateWorkshopTaskAssigneeQuery,
      async onQueryStarted(arg, { dispatch, getState, queryFulfilled }) {
        const patches = workshopTasksApi.util
          .selectInvalidatedBy(getState(), [
            { type: 'workshop-tasks', id: arg.workshopId },
          ])
          .filter(entry => entry.endpointName === 'getWorkshopTasks')
          .map(({ originalArgs }) =>
            dispatch(
              workshopTasksApi.util.updateQueryData(
                'getWorkshopTasks',
                originalArgs as TGetWorkshopTasksRequest,
                draft => {
                  applyOptimisticWorkshopTaskUpdate(draft, arg.taskId, {
                    assignee: arg.userName,
                  });
                },
              ),
            ),
          );
        try {
          await queryFulfilled;
        } catch {
          patches.forEach(patch => patch.undo());
        }
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: 'workshop-tasks', id: arg.workshopId },
      ],
    }),
  }),
});

export const {
  useGetWorkshopTasksQuery,
  useGetWorkshopTaskAssigneesQuery,
  useExportWorkshopTasksMutation,
  useAddCustomWorkshopTaskMutation,
  useDeleteCustomWorkshopTaskMutation,
  useUpdateWorkshopTaskStatusMutation,
  useUpdateWorkshopTaskDueDateMutation,
  useUpdateWorkshopTaskNameMutation,
  useUpdateWorkshopTaskAssigneeMutation,
} = workshopTasksApi;
